import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import QRCode from "qrcode";
import SettingsTab from "./SettingsTab.jsx";
import { SearchableCombobox, BizAvatar } from "./SearchableCombobox.jsx";
import { authedJson, publicJson } from "../apiClient.js";
import { useToast } from "../toast/ToastProvider.jsx";
import { matchesLinkSearch, linkStatusLabel } from "../linkSearch.js";

// ═════════════════════════════════════════════════════════════════════════════
// SHARED STYLES  — matches BookingPlatform design system
// ═════════════════════════════════════════════════════════════════════════════
const S = {
  label: { fontSize: 12, fontWeight: 600, color: "#64748B", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6, display: "block" },
  input: { width: "100%", padding: "10px 13px", borderRadius: 8, border: "1.5px solid #E2E8F0", fontSize: 14, color: "#0F172A", outline: "none", transition: "border .15s", background: "#fff", boxSizing: "border-box" },
  btn: (v = "primary", off = false) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: v === "primary" ? "11px 24px" : "10px 20px", borderRadius: 10, fontWeight: 600,
    fontSize: 14, cursor: off ? "not-allowed" : "pointer",
    border: v === "primary" ? "none" : "1.5px solid #E2E8F0",
    background: v === "primary" ? (off ? "#7aaa9e" : "#004e42") : "#fff",
    color: v === "primary" ? "#fff" : (off ? "#CBD5E1" : "#374151"), transition: "all .15s",
  }),
};

const Ic = {
  Loader: ({ s = 18 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" style={{ animation: "spin .75s linear infinite" }}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="48" strokeDashoffset="20" strokeLinecap="round" /></svg>,
  Search: ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3" /><path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  ChevL: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M10 3L6 8l4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  ChevR: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M6 3l4 5-4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Plus: ({ s = 16, c = "currentColor" }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke={c} strokeWidth="1.5" strokeLinecap="round" /></svg>,
  Link: ({ s = 16, c = "currentColor" }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M6.5 9.5l3-3M5 10.5l-1.3 1.3a1.5 1.5 0 002.1 2.1l1.3-1.3M9 5.5l1.3-1.3a1.5 1.5 0 012.1 2.1L11 7.6" stroke={c} strokeWidth="1.3" strokeLinecap="round" /></svg>,
  Sort: ({ s = 12 }) => <svg width={s} height={s} viewBox="0 0 12 12" fill="none"><path d="M3 4.5l3-3 3 3M3 7.5l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Home: ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2 8l6-6 6 6M4 7v5.5a1 1 0 001 1h2v-3h2v3h2a1 1 0 001-1V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Logout: ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M6 2H4a2 2 0 00-2 2v8a2 2 0 002 2h2M10.5 11.5L14 8l-3.5-3.5M14 8H6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Copy: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" /><path d="M3 11V3.5A1.5 1.5 0 014.5 2H11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  Edit: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Trash: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M3 4.5h10M5.5 4.5V3a1 1 0 011-1h3a1 1 0 011 1v1.5M6.5 7v4M9.5 7v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /><path d="M4 4.5l.5 8.5a1 1 0 001 1h5a1 1 0 001-1l.5-8.5" stroke="currentColor" strokeWidth="1.2" /></svg>,
  XMark: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  QR: ({ s = 16, c = "currentColor" }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="5" height="5" rx="1" stroke={c} strokeWidth="1.2"/><rect x="9.5" y="1.5" width="5" height="5" rx="1" stroke={c} strokeWidth="1.2"/><rect x="1.5" y="9.5" width="5" height="5" rx="1" stroke={c} strokeWidth="1.2"/><rect x="10" y="10" width="1.5" height="1.5" fill={c}/><rect x="13" y="10" width="1.5" height="1.5" fill={c}/><rect x="10" y="13" width="1.5" height="1.5" fill={c}/><rect x="13" y="13" width="1.5" height="1.5" fill={c}/><rect x="3" y="3" width="2" height="2" rx=".4" fill={c}/><rect x="11" y="3" width="2" height="2" rx=".4" fill={c}/><rect x="3" y="11" width="2" height="2" rx=".4" fill={c}/></svg>,
  Download: ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 2v8.5M5 8l3 3 3-3M3 12.5h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Toggle: ({ s = 14, on = true }) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="1" y="4" width="14" height="8" rx="4" fill={on ? "#22C55E" : "#E2E8F0"}/><circle cx={on ? "11" : "5"} cy="8" r="3" fill="#fff"/></svg>,
  Cog: ({ s = 16 }) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
};


// ── Slug helper — converts display name to URL-safe slug ───────────────────
function toSlug(str) {
  return str
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")           // spaces → hyphens
    .replace(/[^a-z0-9\-]/g, "")    // remove non-alphanumeric (except hyphens)
    .replace(/--+/g, "-")           // collapse multiple hyphens
    .replace(/^-|-$/g, "");         // trim leading/trailing hyphens
}

// ── ULID generator (Crockford Base32, monotonic within same ms) ────────────
const ULID_CHARS = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
function generateULID() {
  const now = Date.now();
  // 10 chars for 48-bit timestamp
  let ts = "";
  let t = now;
  for (let i = 0; i < 10; i++) {
    ts = ULID_CHARS[t % 32] + ts;
    t = Math.floor(t / 32);
  }
  // 16 chars of randomness
  let rand = "";
  const bytes = crypto.getRandomValues(new Uint8Array(10));
  for (let i = 0; i < 10; i++) {
    rand += ULID_CHARS[bytes[i] % 32];
    if (rand.length >= 16) break;
  }
  while (rand.length < 16) rand += ULID_CHARS[Math.floor(Math.random() * 32)];
  return ts + rand;
}

const PAGE_SIZE = 10;

// ═════════════════════════════════════════════════════════════════════════════
// CONFIG PAGE
// ═════════════════════════════════════════════════════════════════════════════
export default function ConfigPage({ isAdmin = false, uiSettings = {}, refreshAuth }) {
  // uiSettings is refreshed by ProtectedRoute (via refreshAuth) after any
  // settings save, so every settings-driven surface here stays live without
  // a page reload.
  const allowMultipleServices = uiSettings.allowMultipleSelection !== false;
  // Per-UTM hide mode. Used to (a) skip rendering hidden inputs in the link
  // builder and (b) drop any stored values from the generated URL.
  const isUtmHidden = (fieldKey) =>
    uiSettings.utmParameters?.[`utm_${fieldKey}`]?.mode === "hide";

  // Bookings businesses the admin has allowed for link-building, picked on
  // the Settings tab. The per-row business picker below is filtered to this
  // set (case-insensitive match on the SMTP id). Empty ⇒ nothing to pick.
  const allowedBusinessEmails = useMemo(() => {
    const list = Array.isArray(uiSettings.bookingsBusinessEmails)
      ? uiSettings.bookingsBusinessEmails
      : [];
    return new Set(list.map((e) => (e || "").trim().toLowerCase()).filter(Boolean));
  }, [uiSettings.bookingsBusinessEmails]);
  const hasAllowedBusinesses = allowedBusinessEmails.size > 0;

  const { instance, accounts } = useMsal();
  const user = accounts[0];
  const toast = useToast();

  // ── Bookings businesses + per-business services ─────────────────────────
  // Services are no longer global — each service row in the link builder
  // picks its own business, so one link can span shared + personal Bookings
  // businesses. We cache services per business to avoid refetching.
  const [bookingBusinesses, setBookingBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [businessesError, setBusinessesError] = useState(null);
  const [businessPhotos, setBusinessPhotos] = useState({});                  // { [businessEmail]: dataUri | null }
  const [servicesByBusiness, setServicesByBusiness] = useState({});          // { [businessId]: service[] }
  const [loadingServicesByBusiness, setLoadingServicesByBusiness] = useState({}); // { [businessId]: bool }

  // ── Form state ───────────────────────────────────────────────────────────
  // Each row = one service mapping: (business, service) pair. Array shape
  // so we can mix businesses in one link and render a list of picker rows.
  const [serviceRows, setServiceRows] = useState([]); // [{ rowId, businessId, serviceId, serviceName }]
  const [serviceMappingId, setServiceMappingId] = useState(() => generateULID());
  const [displayName, setDisplayName] = useState("");
  const [showUtm, setShowUtm] = useState(false);
  const [utm, setUtm] = useState({ source: "", medium: "", campaign: "", term: "", content: "" });
  const [utmLocked, setUtmLocked] = useState({ medium: false, campaign: false }); // locked by campaign selection
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [showUtmWarning, setShowUtmWarning] = useState(null); // "medium" or "campaign" — which field to unlock
  const [creating, setCreating] = useState(false);
  const [editingLink, setEditingLink] = useState(null); // null = create mode, object = editing
  const [deleting, setDeleting] = useState(null); // mek_bookinglinkid being deleted
  const [toggling, setToggling] = useState(null); // mek_bookinglinkid being toggled
  const [confirmDelete, setConfirmDelete] = useState(null); // link object to confirm delete
  const [qrModal, setQrModal] = useState(null); // { url, displayName } for QR modal
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const qrCanvasRef = useRef(null);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  // ── Tab navigation ───────────────────────────────────────────────────────
  // Only admins can access the Settings tab. Non-admins see the Booking Links
  // tab only, and the cog + settings tab button are hidden for them.
  const [activeTab, setActiveTab] = useState("links"); // "links" | "settings"
  useEffect(() => {
    if (!isAdmin && activeTab === "settings") setActiveTab("links");
  }, [isAdmin, activeTab]);

  // ── Data table state (client-side paging/sorting/filtering) ──────────────
  const [allLinks, setAllLinks] = useState([]); // full dataset from mek_bookinglink
  const [loadingTable, setLoadingTable] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdon");
  const [sortDir, setSortDir] = useState("desc");
  const [copiedId, setCopiedId] = useState(null);
  const [hiddenCols, setHiddenCols] = useState(new Set(["mek_linkid", "mek_description", "createdon"]));
  const [colMenuOpen, setColMenuOpen] = useState(false);

  // ── Fetch bookings businesses on mount + their profile photos ──────────
  // Personal Bookings businesses (user mailboxes) have an owner with a photo;
  // shared mailboxes won't and will fall back to initials in the avatar.
  useEffect(() => {
    authedJson(instance, user, "/api/booking-businesses")
      .then((data) => {
        // Alphabetical by displayName (case-insensitive) so the picker and
        // the Settings default both show stable, human-friendly ordering.
        const list = (data.value || []).slice().sort((a, b) =>
          (a.displayName || a.id || "").localeCompare(b.displayName || b.id || "", undefined, { sensitivity: "base" })
        );
        setBookingBusinesses(list);
        setLoadingBusinesses(false);

        const emails = list.map((b) => b.id).filter(Boolean);
        if (emails.length) {
          fetch(`/api/staff-photo?emails=${encodeURIComponent(emails.join(","))}`)
            .then((r) => (r.ok ? r.json() : {}))
            .then((photos) => setBusinessPhotos(photos || {}))
            .catch((err) => console.warn("[ConfigPage] Business photos fetch failed:", err));
        }
      })
      .catch((err) => {
        console.error("[ConfigPage] Booking businesses fetch failed:", err);
        setBusinessesError(err.message || "Could not load Bookings businesses");
        setLoadingBusinesses(false);
        toast.error(err.message || "Could not load Bookings businesses.", { correlationId: err.correlationId });
      });
  }, []);

  // ── Lazy-load services for a specific bookings business ─────────────────
  // Called when a row's business dropdown is set. Cached so switching between
  // businesses (or editing a link) doesn't re-hit the API every render.
  const loadServicesForBusiness = useCallback((businessId) => {
    if (!businessId) return;
    setServicesByBusiness((prev) => {
      if (prev[businessId]) return prev; // already cached
      return prev;
    });
    setLoadingServicesByBusiness((prev) => ({ ...prev, [businessId]: true }));
    publicJson(`/api/services?businessId=${encodeURIComponent(businessId)}`)
      .then((data) => {
        const list = (data.value || data || []).slice().sort((a, b) =>
          (a.displayName || "").localeCompare(b.displayName || "", undefined, { sensitivity: "base" })
        );
        setServicesByBusiness((prev) => ({ ...prev, [businessId]: list }));
        setLoadingServicesByBusiness((prev) => ({ ...prev, [businessId]: false }));
      })
      .catch((err) => {
        console.error(`[ConfigPage] Services fetch failed for ${businessId}:`, err);
        setServicesByBusiness((prev) => ({ ...prev, [businessId]: [] }));
        setLoadingServicesByBusiness((prev) => ({ ...prev, [businessId]: false }));
        toast.error(err.message || "Could not load services for that business.", { correlationId: err.correlationId });
      });
  }, [instance, user, toast]);

  // ── Fetch campaigns on mount ──────────────────────────────────────────────
  useEffect(() => {
    authedJson(instance, user, "/api/campaigns")
      .then((data) => setCampaigns(data.value || []))
      .catch((err) => {
        console.error("[ConfigPage] Campaigns fetch failed:", err);
        toast.error(err.message || "Could not load campaigns.", { correlationId: err.correlationId });
      });
  }, []);

  // ── Fetch all booking links from Dataverse (once, then client-side ops) ─
  const fetchLinks = useCallback(() => {
    setLoadingTable(true);
    authedJson(instance, user, "/api/booking-links")
      .then((data) => {
        setAllLinks(data.value || []);
        setLoadingTable(false);
      })
      .catch((err) => {
        console.error("[ConfigPage] Booking links fetch failed:", err);
        setLoadingTable(false);
        toast.error(err.message || "Could not load booking links.", { correlationId: err.correlationId });
      });
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  // ── Client-side filter → sort → page ────────────────────────────────────
  const filtered = useMemo(() => {
    let result = allLinks;
    if (search.trim()) {
      result = result.filter((m) => matchesLinkSearch(m, search));
    }
    result = [...result].sort((a, b) => {
      const va = a[sortField] ?? "";
      const vb = b[sortField] ?? "";
      const cmp = typeof va === "string" ? va.localeCompare(vb) : va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return result;
  }, [allLinks, search, sortField, sortDir]);

  const totalCount = filtered.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const mappings = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Service-row handlers ─────────────────────────────────────────────────
  // Each row holds one (business, service) pair. Order is preserved by rowId
  // so React can reconcile rows through business/service picker changes.
  const nextRowId = useRef(1);
  const addServiceRow = () => {
    if (!allowMultipleServices && serviceRows.length >= 1) return;
    setServiceRows((prev) => [
      ...prev,
      { rowId: `r${nextRowId.current++}`, businessId: "", serviceId: "", serviceName: "" },
    ]);
    setFormError(null);
    setFormSuccess(null);
  };
  const removeServiceRow = (rowId) => {
    setServiceRows((prev) => prev.filter((r) => r.rowId !== rowId));
    setFormError(null);
    setFormSuccess(null);
  };
  const updateServiceRowBusiness = (rowId, businessId) => {
    setServiceRows((prev) =>
      prev.map((r) =>
        r.rowId === rowId ? { ...r, businessId, serviceId: "", serviceName: "" } : r
      )
    );
    if (businessId) loadServicesForBusiness(businessId);
    setFormError(null);
    setFormSuccess(null);
  };
  const updateServiceRowService = (rowId, serviceId) => {
    setServiceRows((prev) =>
      prev.map((r) => {
        if (r.rowId !== rowId) return r;
        const svc = (servicesByBusiness[r.businessId] || []).find((s) => s.id === serviceId);
        return { ...r, serviceId, serviceName: svc?.displayName || "" };
      })
    );
    setFormError(null);
    setFormSuccess(null);
  };

  // ── Campaign selection → pre-fill UTM medium + campaign ──────────────────
  const handleCampaignSelect = (campaignId) => {
    setSelectedCampaignId(campaignId);
    if (!campaignId) {
      // Cleared — unlock and clear the fields
      setUtm((prev) => ({ ...prev, medium: "", campaign: "" }));
      setUtmLocked({ medium: false, campaign: false });
      return;
    }
    const camp = campaigns.find((c) => c.campaignid === campaignId);
    if (camp) {
      setUtm((prev) => ({
        ...prev,
        medium: camp.typeLabel ? toSlug(camp.typeLabel) : "",
        campaign: camp.name ? toSlug(camp.name) : "",
      }));
      setUtmLocked({ medium: true, campaign: true });
    }
  };

  // ── Unlock UTM field with warning ───────────────────────────────────────
  const handleUtmUnlock = (field) => {
    setShowUtmWarning(field);
  };

  const confirmUtmUnlock = () => {
    if (showUtmWarning) {
      setUtmLocked((prev) => ({ ...prev, [showUtmWarning]: false }));
    }
    setShowUtmWarning(null);
  };

  // ── Build the service URL ────────────────────────────────────────────────
  // Relative path (stored in Dataverse — no host, works across environments)
  const serviceUrlPath = useMemo(() => {
    if (!serviceMappingId) return "";
    const base = `/mek/${serviceMappingId}`;
    if (!showUtm) return base;
    const params = new URLSearchParams();
    // Skip any UTM the admin has marked "hide" in Settings — the booking flow
    // would drop them anyway, so they shouldn't end up in the generated URL.
    if (utm.source.trim()   && !isUtmHidden("source"))   params.set("utm_source", utm.source.trim());
    if (utm.medium.trim()   && !isUtmHidden("medium"))   params.set("utm_medium", utm.medium.trim());
    if (utm.campaign.trim() && !isUtmHidden("campaign")) params.set("utm_campaign", utm.campaign.trim());
    if (utm.term.trim()     && !isUtmHidden("term"))     params.set("utm_term", utm.term.trim());
    if (utm.content.trim()  && !isUtmHidden("content"))  params.set("utm_content", utm.content.trim());
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }, [serviceMappingId, showUtm, utm, uiSettings.utmParameters]);

  // Full URL for display, copy, and QR (host from env or current origin)
  const displayHost = import.meta.env.VITE_BOOKING_HOST_URL || window.location.origin;
  const serviceUrl = serviceUrlPath ? `${displayHost}${serviceUrlPath}` : "";

  // ── Create handler — creates booking link + service mappings ────────────
  const handleCreate = async () => {
    setFormError(null);
    setFormSuccess(null);

    if (!serviceMappingId.trim()) { setFormError("Link ID is required. Click generate."); return; }
    if (!displayName.trim()) { setFormError("Display Name is required."); return; }
    if (!serviceRows.length) { setFormError("Please add at least one service."); return; }
    const incomplete = serviceRows.find((r) => !r.businessId || !r.serviceId);
    if (incomplete) {
      setFormError("Every service row needs both a bookings business and a service selected.");
      return;
    }

    setCreating(true);
    try {
      const svcPayload = serviceRows.map((r) => ({
        id:          r.serviceId,
        displayName: r.serviceName,
        businessId:  r.businessId,
      }));

      const created = await authedJson(instance, user, "/api/booking-links", {
        method: "POST",
        body: JSON.stringify({
          mek_linkid: serviceMappingId.trim(),
          mek_displayname: displayName.trim(),
          mek_url: serviceUrlPath,
          campaignId: selectedCampaignId || undefined,
          services: svcPayload,
        }),
      });

      setFormSuccess("Booking link created successfully!");
      toast.success("Booking link created.");
      setServiceRows([]);
      setServiceMappingId("");
      setDisplayName("");
      setUtm({ source: "", medium: "", campaign: "", term: "", content: "" });
      setUtmLocked({ medium: false, campaign: false });
      setSelectedCampaignId("");
      setShowUtm(false);

      // Insert the new link at the top of the local dataset
      setAllLinks((prev) => [created, ...prev]);
      setPage(0);
    } catch (err) {
      toast.error(err.message || "Failed to create booking link.", { correlationId: err.correlationId });
    } finally {
      setCreating(false);
    }
  };

  // ── Edit handler — populate form from a link record ──────────────────────
  const handleEdit = async (link) => {
    setEditingLink(link);
    setServiceMappingId(link.mek_linkid || "");
    setDisplayName(link.mek_displayname || "");
    setFormError(null);
    setFormSuccess(null);

    // Parse UTM params from URL if present
    try {
      const url = new URL(link.mek_url, window.location.origin);
      const p = url.searchParams;
      const hasUtm = p.has("utm_source") || p.has("utm_medium") || p.has("utm_campaign") || p.has("utm_term") || p.has("utm_content");
      setUtm({
        source: p.get("utm_source") || "",
        medium: p.get("utm_medium") || "",
        campaign: p.get("utm_campaign") || "",
        term: p.get("utm_term") || "",
        content: p.get("utm_content") || "",
      });
      setShowUtm(hasUtm);
    } catch {
      setUtm({ source: "", medium: "", campaign: "", term: "", content: "" });
      setShowUtm(false);
    }

    // Restore linked campaign selection and lock UTM fields
    const campId = link._mek_linkedcampaign_value || "";
    console.log(`[ConfigPage] Edit link:`, { linkId: link.mek_linkid, campId, allKeys: Object.keys(link).filter(k => k.includes("campaign")) });
    setSelectedCampaignId(campId);
    if (campId) {
      setUtmLocked({ medium: true, campaign: true });
      setShowUtm(true);
    } else {
      setUtmLocked({ medium: false, campaign: false });
    }

    // Fetch linked service mappings to pre-fill the (business, service) rows.
    // Each mapping carries its own mek_bookingbusinessid; when empty we leave
    // the row's business unset so the admin can pick it — a legacy-row hint.
    try {
      const data = await authedJson(instance, user, `/api/service-mappings?linkId=${link.mek_bookinglinkid}`);
      const rows = (data.value || [])
        .filter((m) => m.mek_serviceid)
        .map((m) => ({
          rowId:       `r${nextRowId.current++}`,
          businessId:  (m.mek_bookingbusinessid || "").trim() || "",
          serviceId:   m.mek_serviceid,
          serviceName: m.mek_servicename || "",
        }));
      setServiceRows(rows);
      // Warm the per-business services cache so the dropdowns render with
      // selected values immediately instead of flashing "loading".
      for (const b of [...new Set(rows.map((r) => r.businessId).filter(Boolean))]) {
        loadServicesForBusiness(b);
      }
    } catch (err) {
      console.error("[ConfigPage] Failed to fetch linked services:", err);
      toast.error(err.message || "Could not load this link's services.", { correlationId: err.correlationId });
    }

    // Scroll to the form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Cancel edit — reset to create mode ──────────────────────────────────
  const handleCancelEdit = () => {
    setEditingLink(null);
    setServiceMappingId("");
    setDisplayName("");
    setServiceRows([]);
    setUtm({ source: "", medium: "", campaign: "", term: "", content: "" });
    setUtmLocked({ medium: false, campaign: false });
    setSelectedCampaignId("");
    setShowUtm(false);
    setFormError(null);
    setFormSuccess(null);
  };

  // ── Update handler — PATCH the edited link ──────────────────────────────
  const handleUpdate = async () => {
    if (!editingLink) return;
    setFormError(null);
    setFormSuccess(null);

    if (!displayName.trim()) { setFormError("Display Name is required."); return; }
    if (!serviceRows.length) { setFormError("Please add at least one service."); return; }
    if (serviceRows.some((r) => !r.businessId || !r.serviceId)) {
      setFormError("Every service row needs both a bookings business and a service selected.");
      return;
    }

    setCreating(true);
    try {
      // services: the server syncs the link's service mappings to these rows
      // (added / removed / swapped services), same shape as create.
      await authedJson(instance, user, `/api/booking-links/${editingLink.mek_bookinglinkid}`, {
        method: "PATCH",
        body: JSON.stringify({
          mek_displayname: displayName.trim(),
          mek_url: serviceUrlPath,
          campaignId: selectedCampaignId || null,
          services: serviceRows.map((r) => ({ id: r.serviceId, displayName: r.serviceName, businessId: r.businessId })),
        }),
      });

      // Update local dataset
      setAllLinks((prev) => prev.map((l) =>
        l.mek_bookinglinkid === editingLink.mek_bookinglinkid
          ? { ...l, mek_displayname: displayName.trim(), mek_url: serviceUrlPath, _mek_linkedcampaign_value: selectedCampaignId || null }
          : l
      ));
      setFormSuccess("Booking link updated successfully!");
      toast.success("Booking link updated.");
      handleCancelEdit();
    } catch (err) {
      toast.error(err.message || "Failed to update booking link.", { correlationId: err.correlationId });
    } finally {
      setCreating(false);
    }
  };

  // ── Delete handler ──────────────────────────────────────────────────────
  const handleDelete = async (linkId) => {
    setDeleting(linkId);
    try {
      await authedJson(instance, user, `/api/booking-links/${linkId}`, { method: "DELETE" });
      setAllLinks((prev) => prev.filter((l) => l.mek_bookinglinkid !== linkId));
      // If we were editing this link, cancel edit
      if (editingLink?.mek_bookinglinkid === linkId) handleCancelEdit();
      toast.success("Booking link deleted.");
    } catch (err) {
      toast.error(err.message || "Failed to delete booking link.", { correlationId: err.correlationId });
    } finally {
      setDeleting(null);
    }
  };

  // ── Toggle link status (enable/disable) ─────────────────────────────────
  // Dataverse Choice fields use integers: 1 = Active, 0 = Inactive
  const handleToggleStatus = async (link) => {
    const linkId = link.mek_bookinglinkid;
    const currentStatus = link.mek_linkstatus !== 0; // treat null/undefined/1 as active
    const newStatus = currentStatus ? 0 : 1; // Toggle: active(1) -> inactive(0), inactive(0) -> active(1)

    setToggling(linkId);
    setFormError(null); // Clear any previous errors
    try {
      console.log(`[ConfigPage] Toggling status for ${linkId}: ${link.mek_linkstatus} -> ${newStatus}`);
      await authedJson(instance, user, `/api/booking-links/${linkId}`, {
        method: "PATCH",
        body: JSON.stringify({ mek_linkstatus: newStatus }),
      });
      // Update local state
      setAllLinks((prev) =>
        prev.map((l) =>
          l.mek_bookinglinkid === linkId ? { ...l, mek_linkstatus: newStatus } : l
        )
      );
    } catch (err) {
      console.error(`[ConfigPage] Toggle failed:`, err);
      toast.error(err.message || "Failed to update link status.", { correlationId: err.correlationId });
    } finally {
      setToggling(null);
    }
  };

  // ── QR Code generation ──────────────────────────────────────────────────
  const showQR = async (url, name) => {
    setQrModal({ url, displayName: name });
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 400, margin: 2, color: { dark: "#004e42", light: "#ffffff" } });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl(null);
    }
  };

  const downloadQR = () => {
    if (!qrDataUrl || !qrModal) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `qr-${qrModal.displayName || "booking-link"}.png`;
    a.click();
  };


  // ── Sort handler ─────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(0);
  };

  // ── Search with debounce ─────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);
  // Clear immediately (no debounce wait) — the × button and Esc.
  const clearSearch = () => { setSearchInput(""); setSearch(""); setPage(0); };

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // ── Column headers config ────────────────────────────────────────────────
  const allColumns = useMemo(() => [
    { key: "mek_displayname", label: "Topic", sortable: true },
    { key: "mek_linkstatus", label: "Status", sortable: true },
    { key: "mek_linkid", label: "Link ID", sortable: true },
    { key: "mek_url", label: "Booking URL", sortable: false },
    { key: "mek_description", label: "Description", sortable: true },
    { key: "createdon", label: "Created", sortable: true },
  ], []);
  const columns = allColumns.filter((c) => !hiddenCols.has(c.key));
  const toggleCol = (key) => setHiddenCols((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  return (
    <>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        *, *::before, *::after { box-sizing: border-box; }
        button { transition: filter .12s; }
        button:not(:disabled):hover { filter: brightness(.96); }
        button:not(:disabled):active { transform: scale(.99); }
        input::placeholder { color: #CBD5E1; }
        select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M3 4.5l3 3 3-3' stroke='%2364748B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 32px; }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#F5FAFA", fontFamily: "-apple-system,'Segoe UI',system-ui,sans-serif", padding: "32px 16px 80px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, animation: "fadeUp .4s ease both" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src="/logo_symbol.png" alt="Mekorma" style={{ width: 36, height: 36, borderRadius: 10, objectFit: "contain" }} />
              <div>
                <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", margin: 0 }}>
                  <span style={{ color: "#004e42" }}>Mekorma</span> Booking Link Configuration
                </h1>
                <p style={{ fontSize: 12, color: "#94A3B8", margin: 0 }}>Manage booking service links</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {user && (
                <span style={{ fontSize: 12, color: "#64748B", marginRight: 4 }}>
                  {user.name || user.username}
                </span>
              )}
              {isAdmin && (
                <button
                  onClick={() => setActiveTab(activeTab === "settings" ? "links" : "settings")}
                  title={activeTab === "settings" ? "Back to Booking Links" : "Settings"}
                  aria-label="Settings"
                  style={{
                    ...S.btn("secondary"),
                    padding: "8px 10px",
                    fontSize: 13,
                    background: activeTab === "settings" ? "#004e42" : "#fff",
                    color: activeTab === "settings" ? "#fff" : "#374151",
                    borderColor: activeTab === "settings" ? "#004e42" : "#E2E8F0",
                  }}
                >
                  <Ic.Cog s={16} />
                </button>
              )}
              <button onClick={handleLogout} title="Sign out" style={{ ...S.btn("secondary"), padding: "8px 14px", fontSize: 13 }}>
                <Ic.Logout s={14} /> Sign out
              </button>
            </div>
          </div>

          {/* ── Tab bar ─────────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1.5px solid #E2E8F0", paddingBottom: 0 }}>
            {[
              { id: "links",    label: "Booking Links", icon: <Ic.Link s={14} />, visible: true },
              { id: "settings", label: "Settings",      icon: <Ic.Cog s={14} />,  visible: isAdmin },
            ].filter(t => t.visible).map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "10px 16px", marginBottom: -1.5,
                  background: "transparent", border: "none",
                  borderBottom: `2.5px solid ${activeTab === t.id ? "#004e42" : "transparent"}`,
                  color: activeTab === t.id ? "#004e42" : "#64748B",
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          {activeTab === "settings" && (
            <SettingsTab
              instance={instance}
              account={user}
              // After a successful save, re-run the access check so all
              // settings (permissions, UI toggles, etc.) propagate live.
              onSaved={() => refreshAuth?.()}
            />
          )}

          {activeTab === "links" && <>

          {/* ── Create Booking Link Form ────────────────────────────────── */}
          <div style={{ background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 16, padding: "24px 28px", marginBottom: 20, animation: "fadeUp .4s .05s ease both" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              {editingLink ? <Ic.Edit s={18} /> : <Ic.Plus s={18} c="#004e42" />}
              <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>
                {editingLink ? "Edit Booking Link" : "Create Booking Link"}
              </h2>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 20px" }}>
              {/* Service Mapping ID — auto-generated ULID, read-only */}
              <div>
                <label style={S.label}>Service Mapping ID</label>
                <input
                  type="text"
                  value={serviceMappingId}
                  readOnly
                  style={{ ...S.input, fontFamily: "monospace", fontSize: 13, background: "#F8FAFC" }}
                />
              </div>

              {/* Display Name */}
              <div>
                <label style={S.label}>Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Demo Bookings"
                  style={S.input}
                />
              </div>

            </div>

            {/* Services — list of (business, service) rows so one link can
                mix services from shared + personal Bookings businesses */}
            <div style={{ marginTop: 16 }}>
              <label style={S.label}>Services</label>
              {businessesError && (
                <p style={{ fontSize: 12, color: "#DC2626", margin: "0 0 8px" }}>
                  {businessesError} — the admin must grant <code>Bookings.Read.All</code> (application) consent.
                </p>
              )}
              {!hasAllowedBusinesses && (
                <div style={{ padding: "12px 14px", borderRadius: 8, background: "#FFFBEB", border: "1px solid #FDE68A", fontSize: 13, color: "#92400E", margin: "0 0 10px" }}>
                  {isAdmin
                    ? <>No Bookings businesses selected yet. Open <button type="button" onClick={() => setActiveTab("settings")} style={{ background: "none", border: "none", color: "#004e42", fontWeight: 600, padding: 0, cursor: "pointer", textDecoration: "underline" }}>Settings → Integrations</button> and check the ones you want to use.</>
                    : <>No Bookings businesses are configured. Ask an admin to pick at least one in Settings → Integrations.</>}
                </div>
              )}
              {serviceRows.length === 0 && hasAllowedBusinesses && (
                <p style={{ fontSize: 13, color: "#94A3B8", margin: "0 0 8px" }}>
                  {allowMultipleServices
                    ? "No services yet. Add a row to pick a bookings business and a service."
                    : "No service yet. Add one row to pick a bookings business and a service."}
                </p>
              )}
              {serviceRows.map((row) => {
                const rowServices = servicesByBusiness[row.businessId] || [];
                const loadingRowServices = !!loadingServicesByBusiness[row.businessId];
                return (
                  <div
                    key={row.rowId}
                    style={{
                      display: "grid",
                      // minmax(0, 1fr) — prevents the trigger text from blowing
                      // the column past the card's width when the business
                      // email is long; lets ellipsis kick in instead.
                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <SearchableCombobox
                      value={row.businessId}
                      onChange={(v) => updateServiceRowBusiness(row.rowId, v)}
                      loading={loadingBusinesses}
                      disabled={!hasAllowedBusinesses}
                      placeholder={
                        hasAllowedBusinesses
                          ? "— Select bookings business —"
                          : "Configure in Settings first"
                      }
                      options={(() => {
                        // Only offer businesses the admin checked on the
                        // Settings tab. Case-insensitive compare because
                        // Graph occasionally normalises SMTP casing.
                        const allowed = bookingBusinesses.filter((b) =>
                          allowedBusinessEmails.has((b.id || "").toLowerCase())
                        );
                        const base = allowed.map((b) => ({
                          value: b.id,
                          label: b.displayName,
                          sublabel: b.displayName !== b.id ? b.id : "",
                          leadingNode: (
                            <BizAvatar
                              displayName={b.displayName}
                              email={b.id}
                              photoUrl={businessPhotos[b.id] || businessPhotos[b.id?.toLowerCase()]}
                            />
                          ),
                        }));
                        // Legacy-row safety: if the row already has a
                        // businessId that's no longer in the allowed set
                        // (either removed from the tenant or dropped from
                        // Settings), surface it so the admin can see + swap
                        // instead of silently losing the mapping.
                        if (row.businessId && !allowed.some((b) => b.id === row.businessId)) {
                          const inTenant = bookingBusinesses.find((b) => b.id === row.businessId);
                          base.push({
                            value: row.businessId,
                            label: inTenant?.displayName || row.businessId,
                            sublabel: inTenant ? "not in Settings" : "not available",
                            leadingNode: (
                              <BizAvatar
                                displayName={inTenant?.displayName || row.businessId}
                                email={row.businessId}
                                photoUrl={businessPhotos[row.businessId] || businessPhotos[row.businessId?.toLowerCase()]}
                              />
                            ),
                          });
                        }
                        return base;
                      })()}
                    />
                    <SearchableCombobox
                      value={row.serviceId}
                      onChange={(v) => updateServiceRowService(row.rowId, v)}
                      disabled={!row.businessId || loadingRowServices}
                      loading={loadingRowServices}
                      placeholder={!row.businessId ? "Pick a business first" : "— Select service —"}
                      options={(() => {
                        const base = rowServices.map((svc) => ({
                          value: svc.id,
                          label: svc.displayName,
                        }));
                        if (
                          row.serviceId &&
                          !rowServices.some((s) => s.id === row.serviceId) &&
                          !loadingRowServices
                        ) {
                          base.push({
                            value: row.serviceId,
                            label: row.serviceName || row.serviceId,
                            sublabel: "not available",
                          });
                        }
                        return base;
                      })()}
                    />
                    <button
                      type="button"
                      onClick={() => removeServiceRow(row.rowId)}
                      style={{
                        ...S.btn("secondary"),
                        padding: "9px 12px",
                        color: "#DC2626",
                        borderColor: "#FCA5A5",
                      }}
                      title="Remove this service"
                    >
                      <Ic.Trash s={14} />
                    </button>
                  </div>
                );
              })}
              {(allowMultipleServices || serviceRows.length === 0) && (
                <button
                  type="button"
                  onClick={addServiceRow}
                  style={{ ...S.btn("secondary", !hasAllowedBusinesses), marginTop: 4 }}
                  disabled={!hasAllowedBusinesses || (loadingBusinesses && !bookingBusinesses.length)}
                  title={!hasAllowedBusinesses ? "Select Bookings businesses in Settings first" : undefined}
                >
                  <Ic.Plus s={14} />
                  <span>{serviceRows.length === 0 ? "Add service" : "Add another service"}</span>
                </button>
              )}
            </div>

            {/* UTM Parameters (collapsible) */}
            {showUtm && (
              <div style={{ marginTop: 16, padding: "16px 18px", background: "#F8FAFC", borderRadius: 10, border: "1px solid #E2E8F0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 14l4-4M6 10l-2.5-.5L3 7l2 2 4-4 2 2 3-3" stroke="#004e42" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>UTM Parameters</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 16px" }}>
                  {[
                    { key: "source", label: "Source", placeholder: "e.g. google, newsletter" },
                    { key: "medium", label: "Medium", placeholder: "e.g. cpc, email, social", lockable: true },
                    { key: "campaign", label: "Campaign", placeholder: "e.g. spring-promo", lockable: true },
                    { key: "term", label: "Term", placeholder: "e.g. booking+demo" },
                    { key: "content", label: "Content", placeholder: "e.g. header-cta" },
                  ].filter(field => !isUtmHidden(field.key)).map((field) => {
                    const isLocked = field.lockable && utmLocked[field.key];
                    return (
                      <div key={field.key}>
                        <label style={{ ...S.label, fontSize: 11 }}>utm_{field.key}</label>
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input
                            type="text"
                            value={utm[field.key]}
                            onChange={(e) => !isLocked && setUtm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            readOnly={isLocked}
                            placeholder={field.placeholder}
                            style={{ ...S.input, fontSize: 13, padding: "8px 11px", flex: 1, background: isLocked ? "#EFF6FF" : "#fff", color: isLocked ? "#004e42" : "#0F172A" }}
                          />
                          {field.lockable && selectedCampaignId && (
                            <button
                              type="button"
                              onClick={() => isLocked ? handleUtmUnlock(field.key) : setUtmLocked((prev) => ({ ...prev, [field.key]: true }))}
                              title={isLocked ? "Unlock field" : "Lock field"}
                              style={{ background: "none", border: "none", cursor: "pointer", color: isLocked ? "#004e42" : "#94A3B8", padding: 3, display: "flex", flexShrink: 0 }}
                            >
                              {isLocked ? (
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M5.5 7V5a2.5 2.5 0 015 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Campaign dropdown — spans full row */}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ ...S.label, fontSize: 11 }}>Mekorma Campaign</label>
                    <select
                      value={selectedCampaignId}
                      onChange={(e) => handleCampaignSelect(e.target.value)}
                      style={{ ...S.input, fontSize: 13, padding: "8px 11px", cursor: "pointer" }}
                    >
                      <option value="">Select a campaign (optional)</option>
                      {campaigns.map((c) => (
                        <option key={c.campaignid} value={c.campaignid}>
                          {c.name}{c.typeLabel ? ` — ${c.typeLabel}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Booking URL — full width with copy button */}
            <div style={{ marginTop: 16 }}>
              <label style={S.label}>Booking URL</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  value={serviceUrl}
                  readOnly
                  placeholder="Auto-generated booking URL"
                  style={{ ...S.input, flex: 1, background: "#F8FAFC", color: serviceUrl ? "#004e42" : "#CBD5E1", fontFamily: "monospace", fontSize: 13 }}
                />
                <button
                  onClick={() => serviceUrl && copyToClipboard(serviceUrl, "form-url")}
                  disabled={!serviceUrl}
                  title="Copy URL"
                  style={{ ...S.btn("secondary", !serviceUrl), padding: "10px 12px", flexShrink: 0 }}
                >
                  {copiedId === "form-url" ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  ) : (
                    <Ic.Copy s={16} />
                  )}
                </button>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {editingLink ? (
                  <>
                    <button
                      onClick={handleUpdate}
                      disabled={creating || !displayName.trim()}
                      style={S.btn("primary", creating || !displayName.trim())}
                    >
                      {creating ? (
                        <><Ic.Loader s={15} /> Updating...</>
                      ) : (
                        <><Ic.Edit s={14} /> Update Booking Link</>
                      )}
                    </button>
                    <button onClick={handleCancelEdit} style={S.btn("secondary")} type="button">
                      <Ic.XMark s={14} /> Cancel
                    </button>
                    {serviceUrl && (
                      <button onClick={() => showQR(serviceUrl, displayName)} style={S.btn("secondary")} type="button" title="Generate QR Code">
                        <Ic.QR s={14} /> QR Code
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        // Reset form and auto-generate a new Service Mapping ID
                        setEditingLink(null);
                        setServiceRows([]);
                        setServiceMappingId(generateULID());
                        setDisplayName("");
                        setUtm({ source: "", medium: "", campaign: "", term: "", content: "" });
                        setSelectedCampaignId("");
                        setUtmLocked({ medium: false, campaign: false });
                        setShowUtm(false);
                        setFormError(null);
                        setFormSuccess(null);
                      }}
                      style={S.btn("secondary")}
                      type="button"
                    >
                      <Ic.Plus s={14} /> New Link
                    </button>
                    {/* Cancel: bail out of the current edits without submitting.
                        Only meaningful when the form has been touched. */}
                    {(serviceRows.length > 0 || displayName.trim() !== "" || selectedCampaignId || utm.source || utm.medium || utm.campaign || utm.term || utm.content) && (
                      <button
                        type="button"
                        onClick={() => {
                          setServiceRows([]);
                          setServiceMappingId(generateULID());
                          setDisplayName("");
                          setUtm({ source: "", medium: "", campaign: "", term: "", content: "" });
                          setSelectedCampaignId("");
                          setUtmLocked({ medium: false, campaign: false });
                          setShowUtm(false);
                          setFormError(null);
                          setFormSuccess(null);
                        }}
                        style={{ ...S.btn("secondary"), color: "#DC2626", borderColor: "#FECACA" }}
                        title="Clear the form and abandon this draft"
                      >
                        <Ic.XMark s={14} /> Cancel
                      </button>
                    )}
                    <button
                      onClick={handleCreate}
                      disabled={creating || !serviceRows.length || !serviceMappingId.trim() || !displayName.trim()}
                      style={S.btn("primary", creating || !serviceRows.length || !serviceMappingId.trim() || !displayName.trim())}
                    >
                      {creating ? (
                        <><Ic.Loader s={15} /> Creating...</>
                      ) : (
                        <><Ic.Link s={14} c="#fff" /> Create Booking Link</>
                      )}
                    </button>
                  </>
                )}

                {formError && (
                  <span style={{ fontSize: 13, color: "#EF4444", display: "flex", alignItems: "center", gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#EF4444" strokeWidth="1.3" /><path d="M8 5v4M8 11v.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" /></svg>
                    {formError}
                  </span>
                )}
                {formSuccess && (
                  <span style={{ fontSize: 13, color: "#16A34A", display: "flex", alignItems: "center", gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    {formSuccess}
                  </span>
                )}
              </div>

              {/* UTM toggle */}
              <button
                type="button"
                onClick={() => setShowUtm((v) => !v)}
                style={{
                  ...S.btn("secondary"), padding: "8px 14px", fontSize: 12,
                  color: showUtm ? "#004e42" : "#64748B",
                  borderColor: showUtm ? "#004e42" : "#E2E8F0",
                  background: showUtm ? "#E8F2EF" : "#fff",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 14l4-4M6 10l-2.5-.5L3 7l2 2 4-4 2 2 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                UTM Parameters
              </button>
            </div>
          </div>

          {/* ── Data Table ──────────────────────────────────────────────── */}
          <div style={{ background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 16, padding: "24px 28px", animation: "fadeUp .4s .1s ease both" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Ic.Link s={18} c="#004e42" />
                <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>Booking Links</h2>
                <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500 }}>
                  {totalCount} total
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Search */}
                <div style={{ position: "relative", width: 400, maxWidth: "calc(100vw - 120px)" }}>
                  <div style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94A3B8" }}>
                    <Ic.Search s={14} />
                  </div>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Escape" && searchInput) clearSearch(); }}
                    placeholder="Search topic, status or booking URL (full, part or ID)…"
                    aria-label="Search booking links"
                    style={{ ...S.input, paddingLeft: 32, paddingRight: 34, fontSize: 13 }}
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      title="Clear search"
                      aria-label="Clear search"
                      style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: "50%", background: "#F1F5F9", color: "#64748B", cursor: "pointer", padding: 0 }}
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
                    </button>
                  )}
                </div>

                {/* Column visibility toggle */}
                <div style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => setColMenuOpen((o) => !o)}
                    title="Toggle columns"
                    style={{ ...S.btn("secondary"), padding: "9px 10px" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <rect x="2" y="2" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
                      <rect x="10" y="2" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
                      <rect x="2" y="10" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
                      <rect x="10" y="10" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
                    </svg>
                  </button>
                  {colMenuOpen && (
                    <>
                      <div style={{ position: "fixed", inset: 0, zIndex: 9 }} onClick={() => setColMenuOpen(false)} />
                      <div style={{
                        position: "absolute", top: "100%", right: 0, zIndex: 10, marginTop: 4,
                        background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 8,
                        boxShadow: "0 4px 16px rgba(0,0,0,.08)", minWidth: 180, padding: "6px 0",
                      }}>
                        <div style={{ padding: "6px 12px 8px", fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Toggle Columns
                        </div>
                        {allColumns.map((col) => (
                          <label
                            key={col.key}
                            style={{
                              display: "flex", alignItems: "center", gap: 8, padding: "6px 12px",
                              cursor: "pointer", fontSize: 13, color: "#0F172A",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!hiddenCols.has(col.key)}
                              onChange={() => toggleCol(col.key)}
                              style={{ width: 14, height: 14, accentColor: "#004e42", cursor: "pointer" }}
                            />
                            {col.label}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        onClick={() => col.sortable && handleSort(col.key)}
                        style={{
                          textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #E2E8F0",
                          fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          cursor: col.sortable ? "pointer" : "default", userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {col.label}
                          {col.sortable && sortField === col.key && (
                            <span style={{ color: "#004e42", transform: sortDir === "desc" ? "scaleY(-1)" : "none", display: "inline-flex" }}>
                              <Ic.Sort s={10} />
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                    <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "2px solid #E2E8F0", fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em", width: 80 }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTable ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {columns.map((col) => (
                          <td key={col.key} style={{ padding: "12px 12px" }}>
                            <div style={{ height: 16, borderRadius: 4, background: "#F1F5F9", animation: "pulse 1.4s ease-in-out infinite" }} />
                          </td>
                        ))}
                        <td style={{ padding: "12px 12px" }}><div style={{ height: 16, borderRadius: 4, background: "#F1F5F9", animation: "pulse 1.4s ease-in-out infinite" }} /></td>
                      </tr>
                    ))
                  ) : mappings.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "40px 12px", color: "#94A3B8" }}>
                        {search ? "No results found" : "No booking links created yet"}
                      </td>
                    </tr>
                  ) : (
                    mappings.map((m, idx) => {
                      const rowKey = m.mek_bookinglinkid || `row-${idx}`;
                      const isEditing = editingLink?.mek_bookinglinkid === m.mek_bookinglinkid;
                      const isDeleting = deleting === m.mek_bookinglinkid;
                      const isDisabled = m.mek_linkstatus === 0;
                      return (
                        <tr
                          key={rowKey}
                          onClick={() => handleEdit(m)}
                          style={{
                            borderBottom: "1px solid #F1F5F9", cursor: "pointer",
                            background: isEditing ? "#F0FAF0" : isDisabled ? "#FAFAFA" : "transparent",
                            transition: "background .1s",
                            opacity: isDeleting ? 0.5 : isDisabled ? 0.6 : 1,
                          }}
                        >
                          {columns.map((col) => (
                            <td key={col.key} style={{ padding: "12px 12px" }}>
                              {col.key === "mek_displayname" && (
                                <span style={{ fontWeight: 500, color: "#0F172A" }}>{m.mek_displayname}</span>
                              )}
                              {col.key === "mek_linkid" && (
                                <span title={m.mek_linkid} style={{ display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#64748B", fontFamily: "monospace", fontSize: 12, maxWidth: "100%" }}>
                                  {m.mek_linkid}
                                </span>
                              )}
                              {col.key === "mek_linkstatus" && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleToggleStatus(m); }}
                                  disabled={toggling === m.mek_bookinglinkid}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    padding: "4px 10px", borderRadius: 20, border: "none",
                                    fontSize: 12, fontWeight: 500, cursor: toggling === m.mek_bookinglinkid ? "wait" : "pointer",
                                    background: m.mek_linkstatus !== 0 ? "#DCFCE7" : "#F1F5F9",
                                    color: m.mek_linkstatus !== 0 ? "#16A34A" : "#94A3B8",
                                    opacity: toggling === m.mek_bookinglinkid ? 0.6 : 1,
                                    transition: "all .15s",
                                  }}
                                >
                                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                                  {linkStatusLabel(m)}
                                </button>
                              )}
                              {col.key === "mek_url" && (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span title={`${displayHost}${m.mek_url || ""}`} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#004e42", fontSize: 12, fontFamily: "monospace", flex: 1 }}>
                                    {`${displayHost}${m.mek_url || ""}`}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(`${displayHost}${m.mek_url || ""}`, m.mek_bookinglinkid); }}
                                    title="Copy URL"
                                    style={{ background: "none", border: "none", cursor: "pointer", color: copiedId === m.mek_bookinglinkid ? "#16A34A" : "#94A3B8", padding: 2, display: "flex" }}
                                  >
                                    {copiedId === m.mek_bookinglinkid ? (
                                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    ) : (
                                      <Ic.Copy />
                                    )}
                                  </button>
                                </div>
                              )}
                              {col.key === "mek_description" && (
                                <span style={{ color: "#64748B", fontSize: 13 }}>{m.mek_description || "—"}</span>
                              )}
                              {col.key === "createdon" && (
                                <span style={{ color: "#94A3B8", fontSize: 12 }}>
                                  {m.createdon ? new Date(m.createdon).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                                </span>
                              )}
                            </td>
                          ))}
                          {/* Actions column */}
                          <td style={{ padding: "12px 8px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                              <button
                                onClick={() => showQR(`${displayHost}${m.mek_url || ""}`, m.mek_displayname)}
                                title="QR Code"
                                style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: 4, display: "flex", borderRadius: 4 }}
                              >
                                <Ic.QR s={14} />
                              </button>
                              <button
                                onClick={() => handleEdit(m)}
                                title="Edit"
                                style={{ background: "none", border: "none", cursor: "pointer", color: isEditing ? "#004e42" : "#94A3B8", padding: 4, display: "flex", borderRadius: 4 }}
                              >
                                <Ic.Edit s={14} />
                              </button>
                              <button
                                onClick={() => setConfirmDelete(m)}
                                title="Delete"
                                disabled={isDeleting}
                                style={{ background: "none", border: "none", cursor: isDeleting ? "wait" : "pointer", color: "#EF4444", padding: 4, display: "flex", borderRadius: 4, opacity: isDeleting ? 0.5 : 0.6 }}
                              >
                                <Ic.Trash s={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, paddingTop: 16, borderTop: "1px solid #F1F5F9" }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    style={{ ...S.btn("secondary", page === 0), padding: "6px 10px", fontSize: 12 }}
                  >
                    <Ic.ChevL s={12} /> Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    // Show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (page < 3) {
                      pageNum = i;
                    } else if (page > totalPages - 4) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        style={{
                          width: 32, height: 32, borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600,
                          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                          background: page === pageNum ? "#004e42" : "transparent",
                          color: page === pageNum ? "#fff" : "#64748B",
                        }}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    style={{ ...S.btn("secondary", page >= totalPages - 1), padding: "6px 10px", fontSize: 12 }}
                  >
                    Next <Ic.ChevR s={12} />
                  </button>
                </div>
              </div>
            )}
          </div>

          </>}

          {/* Footer */}
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <p style={{ fontSize: 11, color: "#CBD5E1" }}>
              <span style={{ color: "#88c96c", fontWeight: 700 }}>Mekorma</span> · Configuration Panel · Dynamics 365
            </p>
          </div>
        </div>
      </div>

      {/* ── Delete confirmation modal ──────────────────────────────── */}
      {confirmDelete && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,.4)", backdropFilter: "blur(4px)" }}
          onClick={() => !deleting && setConfirmDelete(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 16, padding: "32px 36px 28px", maxWidth: 400, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.15)", animation: "fadeUp .2s ease both" }}
          >
            {/* Icon */}
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Ic.Trash s={22} />
            </div>

            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", textAlign: "center", margin: "0 0 8px" }}>
              Delete Booking Link
            </h3>
            <p style={{ fontSize: 14, color: "#64748B", textAlign: "center", margin: "0 0 8px", lineHeight: 1.6 }}>
              Are you sure you want to delete <strong style={{ color: "#0F172A" }}>{confirmDelete.mek_displayname}</strong>?
            </p>
            <p style={{ fontSize: 12, color: "#94A3B8", textAlign: "center", margin: "0 0 24px" }}>
              This will also remove all linked service mappings. This action cannot be undone.
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={!!deleting}
                style={{ ...S.btn("secondary"), padding: "10px 24px", flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleDelete(confirmDelete.mek_bookinglinkid);
                  setConfirmDelete(null);
                }}
                disabled={!!deleting}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: "10px 24px", borderRadius: 10, fontWeight: 600, fontSize: 14,
                  border: "none", cursor: deleting ? "not-allowed" : "pointer",
                  background: deleting ? "#FCA5A5" : "#EF4444", color: "#fff",
                  transition: "all .15s", flex: 1,
                }}
              >
                {deleting ? (
                  <><Ic.Loader s={15} /> Deleting...</>
                ) : (
                  <><Ic.Trash s={14} /> Delete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR Code modal ──────────────────────────────────────────── */}
      {qrModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,.4)", backdropFilter: "blur(4px)" }}
          onClick={() => { setQrModal(null); setQrDataUrl(null); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 16, padding: "36px 40px 32px", maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.15)", animation: "fadeUp .2s ease both", textAlign: "center" }}
          >
            {/* Icon */}
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#E8F2EF", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Ic.QR s={22} c="#004e42" />
            </div>

            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", margin: "0 0 6px" }}>
              QR Code
            </h3>
            <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 20px" }}>
              {qrModal.displayName}
            </p>

            {/* QR image */}
            {qrDataUrl ? (
              <div style={{ padding: 16, background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 12, display: "inline-block", marginBottom: 16 }}>
                <img ref={qrCanvasRef} src={qrDataUrl} alt="QR Code" style={{ width: 240, height: 240, display: "block" }} />
              </div>
            ) : (
              <div style={{ padding: 40, color: "#94A3B8", fontSize: 13 }}>Generating...</div>
            )}

            {/* URL preview */}
            <p style={{ fontSize: 11, color: "#94A3B8", fontFamily: "monospace", margin: "0 0 20px", wordBreak: "break-all", lineHeight: 1.5 }}>
              {qrModal.url}
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => { setQrModal(null); setQrDataUrl(null); }}
                style={{ ...S.btn("secondary"), padding: "10px 24px", flex: 1 }}
              >
                Close
              </button>
              <button
                onClick={downloadQR}
                disabled={!qrDataUrl}
                style={{ ...S.btn("primary", !qrDataUrl), padding: "10px 24px", flex: 1 }}
              >
                <Ic.Download s={14} /> Download PNG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── UTM unlock warning modal ───────────────────────────────── */}
      {showUtmWarning && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,.4)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowUtmWarning(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 16, padding: "32px 36px 28px", maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.15)", animation: "fadeUp .2s ease both" }}
          >
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#FFF7ED", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <svg width="24" height="24" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#F59E0B" strokeWidth="1.3"/><path d="M8 5v4M8 11v.5" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", textAlign: "center", margin: "0 0 8px" }}>
              Override Campaign Value?
            </h3>
            <p style={{ fontSize: 14, color: "#64748B", textAlign: "center", margin: "0 0 8px", lineHeight: 1.6 }}>
              The <strong style={{ color: "#0F172A" }}>utm_{showUtmWarning}</strong> field was auto-filled from the selected campaign.
            </p>
            <p style={{ fontSize: 13, color: "#94A3B8", textAlign: "center", margin: "0 0 24px", lineHeight: 1.5 }}>
              Manually overriding this value may cause mismatches between your UTM tracking and the linked Dynamics campaign. Please ensure you are providing accurate information to prevent unwanted behaviour and bad data issues.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => setShowUtmWarning(null)}
                style={{ ...S.btn("secondary"), padding: "10px 24px", flex: 1 }}
              >
                Keep Locked
              </button>
              <button
                onClick={confirmUtmUnlock}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: "10px 24px", borderRadius: 10, fontWeight: 600, fontSize: 14,
                  border: "none", cursor: "pointer",
                  background: "#F59E0B", color: "#fff", transition: "all .15s", flex: 1,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="7" rx="1.5" stroke="#fff" strokeWidth="1.2"/><path d="M5.5 7V5a2.5 2.5 0 015 0" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/></svg>
                Unlock &amp; Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
